import { useState } from "react";
import { Plus } from "lucide-react";
import { Button } from "@akuna/ui";
import { useTranslation } from "react-i18next";
import { useStayPlans, type StayPlan } from "@/hooks/useStayPlans";
import type { StayPlanModule } from "@/lib/daycare-hotel/stayPlans";
import { AddTile, SectionCard, SectionHeader } from "../accommodations/primitives";
import StayPlanCard from "./StayPlanCard";
import StayPlanDialog from "./StayPlanDialog";

export default function StayPackagesSection({
  hasDaycare,
  hasHotel,
  canCreateDaycare,
  canCreateHotel,
  canEditDaycare,
  canEditHotel,
  canDeleteDaycare,
  canDeleteHotel,
}: {
  hasDaycare: boolean;
  hasHotel: boolean;
  canCreateDaycare: boolean;
  canCreateHotel: boolean;
  canEditDaycare: boolean;
  canEditHotel: boolean;
  canDeleteDaycare: boolean;
  canDeleteHotel: boolean;
}) {
  const { t, i18n } = useTranslation("daycareHotel");
  const { data: daycarePlans, isLoading: isLoadingDaycare } = useStayPlans(hasDaycare ? "daycare" : null);
  const { data: hotelPlans, isLoading: isLoadingHotel } = useStayPlans(hasHotel ? "hotel" : null);
  const [editing, setEditing] = useState<StayPlan | null>(null);
  const [open, setOpen] = useState(false);

  const money = (value: number) =>
    new Intl.NumberFormat(i18n.language.startsWith("pt") ? "pt-BR" : "en-US", { style: "currency", currency: "BRL" }).format(value);

  const packages = [
    ...(daycarePlans ?? []).filter((plan) => plan.kind === "day_package"),
    ...(hotelPlans ?? []).filter((plan) => plan.kind === "day_package"),
  ];
  const isLoading = (hasDaycare && isLoadingDaycare) || (hasHotel && isLoadingHotel);
  const showModuleBadge = hasDaycare && hasHotel;
  const tone = hasDaycare ? "daycare" : "hotel";

  const creatableModules: StayPlanModule[] = [
    ...(hasDaycare && canCreateDaycare ? (["daycare"] as const) : []),
    ...(hasHotel && canCreateHotel ? (["hotel"] as const) : []),
  ];

  const start = (plan: StayPlan | null) => {
    setEditing(plan);
    setOpen(true);
  };

  const canEditPlan = (plan: StayPlan) => (plan.module === "hotel" ? canEditHotel : canEditDaycare);
  const canDeletePlan = (plan: StayPlan) => (plan.module === "hotel" ? canDeleteHotel : canDeleteDaycare);

  const title = hasDaycare && hasHotel ? t("packages.titleBoth") : hasHotel ? t("packages.titleHotel") : t("packages.titleDaycare");

  return (
    <section>
      <SectionHeader
        tone={tone}
        badge={t("packages.badge")}
        title={title}
        description={t("packages.description")}
        action={creatableModules.length > 0 ? (
          <Button size="sm" variant="outline" onClick={() => start(null)}>
            <Plus className="mr-1.5 h-4 w-4" />
            {t("packages.new")}
          </Button>
        ) : undefined}
      />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {isLoading
          ? [0, 1].map((key) => <SectionCard key={key} className="h-40 animate-pulse bg-muted/40" />)
          : packages.map((plan) => (
              <StayPlanCard
                key={plan.id}
                plan={plan}
                canEdit={canEditPlan(plan)}
                canDelete={canDeletePlan(plan)}
                onEdit={() => start(plan)}
                money={money}
                showModuleBadge={showModuleBadge}
              />
            ))}
        {creatableModules.length > 0 ? (
          <AddTile onClick={() => start(null)} tone={tone} minHeight="160px" icon={<Plus className="h-5 w-5" />} title={t("packages.new")} />
        ) : null}
      </div>
      <StayPlanDialog
        open={open}
        onOpenChange={setOpen}
        kind="day_package"
        allowedModules={editing ? [editing.module] : creatableModules}
        plan={editing}
      />
    </section>
  );
}
